/**
 * Avatar — renders a user's uploaded photo, falling back to initials in a
 * colored circle when there's no image (or it fails to load).
 *
 * Color derives from a stable hash of the user id so each user gets
 * a consistent color across the SPA. Sizes match the brandbook's
 * .avatar-{xs,sm,md,lg} variants.
 */
import { useEffect, useState } from "react";

// Backgrounds are darkened to AA shades so the white initials clear 4.5:1
// contrast (the prior brighter palette failed axe color-contrast).
const COLORS = [
  "#157049", // green
  "#1d4ed8", // blue
  "#c2410c", // orange
  "#7e22ce", // purple
  "#b45309", // amber
  "#0e7490", // cyan
  "#b91c1c", // red
  "#4d7c0f", // lime
];

function colorFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return COLORS[Math.abs(h) % COLORS.length] ?? COLORS[0]!;
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

interface AvatarProps {
  id: string;
  name: string;
  size?: number | "xs" | "sm" | "md" | "lg";
  /** Uploaded photo URL. When set and loadable, it replaces the initials. */
  image?: string | null;
}

export function Avatar({ id, name, size = "sm", image }: AvatarProps) {
  // Reset the broken flag whenever the URL changes (e.g. after a re-upload),
  // so a freshly-uploaded photo gets a fresh chance to load.
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [image]);
  const showImg = !!image && !broken;

  const inner = showImg ? (
    <img
      src={image}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
        borderRadius: "inherit",
      }}
    />
  ) : (
    initialsFor(name)
  );

  if (typeof size === "number") {
    return (
      <div
        className="avatar"
        style={{
          background: colorFor(id),
          color: "#fff",
          width: size,
          height: size,
          fontSize: Math.round(size * 0.42),
          overflow: "hidden",
        }}
        title={name}
        aria-label={name}
      >
        {inner}
      </div>
    );
  }
  return (
    <div
      className={`avatar avatar-${size}`}
      style={{ background: colorFor(id), color: "#fff", overflow: "hidden" }}
      title={name}
      aria-label={name}
    >
      {inner}
    </div>
  );
}
